/// <reference types="vite/client" />

export const HOSTED_DEVELOPMENT_CONVEX_URL =
  "https://curious-narwhal-638.convex.cloud";

export type NkuliEnvironment = "local" | "preview" | "production";

export type EnvironmentInput = {
  environment?: string;
  convexUrl?: string;
  convexDeploymentKind?: string;
  clerkPublishableKey?: string;
  clerkIssuerUrl?: string;
};

export type EnvironmentConfig = {
  environment: NkuliEnvironment;
  convexUrl: string;
  clerkPublishableKey: string;
  clerkIssuerUrl?: string;
};

export class EnvironmentConfigurationError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "EnvironmentConfigurationError";
    this.code = code;
  }
}

export function resolveEnvironmentName(
  input: EnvironmentInput,
): string | undefined {
  if (input.environment) return input.environment;

  const normalizedConvexUrl = input.convexUrl?.replace(/\/$/, "");
  if (
    normalizedConvexUrl === HOSTED_DEVELOPMENT_CONVEX_URL &&
    input.clerkPublishableKey?.startsWith("pk_test_")
  ) {
    return "preview";
  }

  return undefined;
}

function requireEnvironment(value: string | undefined): NkuliEnvironment {
  if (!value) {
    throw new EnvironmentConfigurationError(
      "ENVIRONMENT_REQUIRED",
      "VITE_NKULI_ENV must be declared as local, preview, or production.",
    );
  }

  if (value !== "local" && value !== "preview" && value !== "production") {
    throw new EnvironmentConfigurationError(
      "ENVIRONMENT_UNSUPPORTED",
      `Unsupported Nkuli environment: ${value}.`,
    );
  }

  return value;
}

function requireUrl(value: string | undefined, label: string): URL {
  if (!value) {
    throw new EnvironmentConfigurationError(
      `${label.toUpperCase()}_REQUIRED`,
      `${label} is required for the declared environment.`,
    );
  }

  try {
    return new URL(value);
  } catch {
    throw new EnvironmentConfigurationError(
      `${label.toUpperCase()}_INVALID`,
      `${label} must be an absolute URL.`,
    );
  }
}

function requireClerkKey(value: string | undefined): string {
  if (!value) {
    throw new EnvironmentConfigurationError(
      "CLERK_KEY_REQUIRED",
      "VITE_CLERK_PUBLISHABLE_KEY is required for the declared environment.",
    );
  }

  if (!value.startsWith("pk_test_") && !value.startsWith("pk_live_")) {
    throw new EnvironmentConfigurationError(
      "CLERK_KEY_INVALID",
      "VITE_CLERK_PUBLISHABLE_KEY must be a Clerk publishable key.",
    );
  }

  return value;
}

function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]"
  );
}

function isDevelopmentConvexUrl(url: URL): boolean {
  return (
    isLocalHost(url.hostname) ||
    url.hostname === "curious-narwhal-638.convex.cloud"
  );
}

function isConvexCloudUrl(url: URL): boolean {
  return (
    url.protocol === "https:" &&
    /^[a-z0-9-]+\.convex\.cloud$/.test(url.hostname)
  );
}

function isDevelopmentIssuer(issuerUrl: URL | undefined): boolean {
  return (
    !issuerUrl ||
    (issuerUrl.protocol === "https:" &&
      issuerUrl.hostname.endsWith(".clerk.accounts.dev"))
  );
}

function validateLocalCompatibility(
  convexDeploymentKind: string | undefined,
  clerkKey: string,
  clerkIssuerUrl: URL | undefined,
) {
  if (
    (convexDeploymentKind && convexDeploymentKind !== "development") ||
    !clerkKey.startsWith("pk_test_") ||
    !isDevelopmentIssuer(clerkIssuerUrl)
  ) {
    throw new EnvironmentConfigurationError(
      "LOCAL_PROVIDER_MISMATCH",
      "Local Nkuli development requires Clerk Development credentials and issuer.",
    );
  }
}

function validatePreviewCompatibility(
  convexUrl: URL,
  convexDeploymentKind: string | undefined,
  clerkKey: string,
  clerkIssuerUrl: URL | undefined,
) {
  const isHostedDevelopment =
    !convexDeploymentKind || convexDeploymentKind === "development";
  const isIsolatedPreview = convexDeploymentKind === "preview";
  const validConvexTarget =
    (isHostedDevelopment && isDevelopmentConvexUrl(convexUrl)) ||
    (isIsolatedPreview &&
      !isDevelopmentConvexUrl(convexUrl) &&
      isConvexCloudUrl(convexUrl));

  if (
    !validConvexTarget ||
    !clerkKey.startsWith("pk_test_") ||
    !isDevelopmentIssuer(clerkIssuerUrl)
  ) {
    throw new EnvironmentConfigurationError(
      "PREVIEW_PROVIDER_MISMATCH",
      "Preview/hosted testing must use Convex Development or an isolated Convex Preview deployment with Clerk Development.",
    );
  }
}

function validateProductionCompatibility(
  convexUrl: URL,
  clerkKey: string,
  clerkIssuerUrl: URL | undefined,
) {
  const isDevelopmentIssuerUrl =
    clerkIssuerUrl?.hostname.endsWith(".clerk.accounts.dev") ?? false;
  if (
    convexUrl.protocol !== "https:" ||
    !clerkIssuerUrl ||
    clerkIssuerUrl.protocol !== "https:" ||
    !isConvexCloudUrl(convexUrl) ||
    isDevelopmentConvexUrl(convexUrl) ||
    clerkKey.startsWith("pk_test_") ||
    isDevelopmentIssuerUrl
  ) {
    throw new EnvironmentConfigurationError(
      "PRODUCTION_PROVIDER_MISMATCH",
      "Production cannot use a local or development Convex target or Clerk issuer.",
    );
  }
}

function validateEnvironmentCompatibility(
  environment: NkuliEnvironment,
  convexUrl: URL,
  convexDeploymentKind: string | undefined,
  clerkKey: string,
  clerkIssuerUrl: URL | undefined,
) {
  if (environment === "local") {
    validateLocalCompatibility(convexDeploymentKind, clerkKey, clerkIssuerUrl);
    return;
  }

  if (environment === "preview") {
    validatePreviewCompatibility(
      convexUrl,
      convexDeploymentKind,
      clerkKey,
      clerkIssuerUrl,
    );
    return;
  }

  validateProductionCompatibility(convexUrl, clerkKey, clerkIssuerUrl);
}

export function validateEnvironment(
  input: EnvironmentInput,
): EnvironmentConfig {
  const environment = requireEnvironment(input.environment);
  const convexUrl = requireUrl(input.convexUrl, "VITE_CONVEX_URL");
  const clerkKey = requireClerkKey(input.clerkPublishableKey);
  const clerkIssuerUrl = input.clerkIssuerUrl
    ? requireUrl(input.clerkIssuerUrl, "VITE_CLERK_ISSUER_URL")
    : undefined;

  validateEnvironmentCompatibility(
    environment,
    convexUrl,
    input.convexDeploymentKind,
    clerkKey,
    clerkIssuerUrl,
  );

  return {
    environment,
    convexUrl: convexUrl.toString().replace(/\/$/, ""),
    clerkPublishableKey: clerkKey,
    ...(clerkIssuerUrl
      ? { clerkIssuerUrl: clerkIssuerUrl.toString().replace(/\/$/, "") }
      : {}),
  };
}

export function getBrowserEnvironment(): EnvironmentConfig {
  const input = {
    environment: import.meta.env.VITE_NKULI_ENV,
    convexUrl: import.meta.env.VITE_CONVEX_URL,
    convexDeploymentKind: import.meta.env.VITE_CONVEX_DEPLOYMENT_KIND,
    clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
    clerkIssuerUrl: import.meta.env.VITE_CLERK_ISSUER_URL,
  };

  return validateEnvironment({
    ...input,
    environment: resolveEnvironmentName(input),
  });
}
